'use client';

import { useEffect, useState } from 'react';

import { FeatureReviewCatalogBase } from '@/fo/feature/review/catalog/base';
import { FeatureReviewDetailBase } from '@/fo/feature/review/detail/base';
import {
  FeatureReviewFormBase,
  RatingValue,
  UserInfo
} from '@/fo/feature/review/form/base';
import { Option } from '@/fo/ui/kit/listbox/base';
import {
  QuestionsData,
  ReviewsData,
  ReviewTab,
  Product,
  Question,
  Review
} from '@/fo/ui/review/catalog/base';
import { UiReviewDetailBase } from '@/fo/ui/review/detail/base';
import { UiReviewLightboxBase } from '@/fo/ui/review/lightbox/base';
import { UiReviewSectionBase } from '@/fo/ui/review/section/base';

import { getDictionaries } from '../../api/getDictionaries';
import { getUserInfo } from '../../api/getUserInfo';

export interface ReviewSectionClientProps {
  reviewsData: ReviewsData;
  questionsData: QuestionsData;
  product: Product;
  productMindboxId: string;
  isAuthorized: boolean;
}

export function ReviewSectionClient({
  reviewsData,
  questionsData,
  product,
  productMindboxId,
  isAuthorized
}: ReviewSectionClientProps) {
  const [openForm, setOpenForm] = useState<ReviewTab>();

  const [skinTypes, setSkinTypes] = useState<Option[]>([]);
  const [skinProblems, setSkinProblems] = useState<Option[]>([]);
  const [ratingId, setRatingId] = useState<string>('');
  const [ratings, setRatings] = useState<RatingValue[]>([]);
  const [userInfo, setUserInfo] = useState<UserInfo>();

  const [isLoading, setIsLoading] = useState(false);

  const [lightboxImageId, setLightboxImageId] = useState<string | null>(null);
  const [catalogOpen, setCatalogOpen] = useState<ReviewTab | null>();
  const [reviewId, setReviewId] = useState<string | null>(null);

  const [fullReview, setFullReview] = useState<Review | null>(null);
  const [fullQuestion, setFullQuestion] = useState<Question | null>(null);

  const handleOpenForm = (type: ReviewTab) => {
    setOpenForm(type);
  };

  const handleOpenChange = (open: boolean) => {
    setOpenForm(open ? openForm : undefined);
  };

  const handleCloseCatalog = () => {
    setCatalogOpen(null);
  };

  const handleOpenCatalog = (open: boolean) => {
    setCatalogOpen(open ? catalogOpen : null);
  };

  const fetchDictionaries = async () => {
    const { skinTypes, skinProblems, ratings, ratingId } =
      await getDictionaries();

    const skinTypesOptions =
      skinTypes
        ?.filter((item) => item?.code && item?.label)
        .map((item) => ({
          label: item?.label || '',
          value: item?.code || ''
        })) || [];

    const skinProblemsOptions =
      skinProblems
        ?.filter((item) => item?.code && item?.label)
        .map((item) => ({
          label: item?.label || '',
          value: item?.code || ''
        })) || [];

    setSkinTypes(skinTypesOptions);
    setSkinProblems(skinProblemsOptions);
    setRatingId(ratingId);
    setRatings(ratings);
  };

  const fetchUserInfo = async () => {
    const { userInfo } = await getUserInfo();

    setUserInfo(userInfo);
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      await fetchDictionaries();

      if (isAuthorized) {
        await fetchUserInfo();
      }

      setIsLoading(false);
    };

    fetchInitialData();
  }, [isAuthorized]);

  return (
    <>
      <UiReviewSectionBase
        id="review"
        reviewsData={reviewsData}
        questionsData={questionsData}
        isNewProduct={product.isNewProduct}
        onActionButtonClick={handleOpenForm}
        onViewAllClick={(tab) => {
          setCatalogOpen(tab);
        }}
        onImageClick={setLightboxImageId}
        onReviewClick={setFullReview}
        onQuestionClick={setFullQuestion}
      />
      <UiReviewLightboxBase
        images={reviewsData.images.map((image) => ({
          id: image.id,
          src: image.url,
          alt: image.alt,
          review: {
            id: image.reviewId
          }
        }))}
        selectedIndex={
          lightboxImageId
            ? reviewsData.images.findIndex(
                (image) => image.id === lightboxImageId
              )
            : null
        }
        title={product.title}
        open={!!lightboxImageId}
        onOpenChange={(open) => {
          if (!open) {
            setLightboxImageId(null);
          }
        }}
        onOpenReview={(reviewId) => {
          setReviewId(reviewId);
        }}
      />
      <UiReviewDetailBase
        open={!!fullReview || !!fullQuestion}
        onOpenChange={(open) => {
          if (!open) {
            setFullReview(null);
            setFullQuestion(null);
          }
        }}
        review={fullReview ?? undefined}
        question={fullQuestion ?? undefined}
      />
      <FeatureReviewDetailBase
        reviewId={reviewId}
        onClose={() => setReviewId(null)}
      />
      <FeatureReviewCatalogBase
        reviewsData={reviewsData}
        questionsData={questionsData}
        product={product}
        open={catalogOpen}
        onOpenChange={handleOpenCatalog}
        onImageClick={setLightboxImageId}
        onClose={handleCloseCatalog}
        onActionClick={handleOpenForm}
      />
      <FeatureReviewFormBase
        isLoading={isLoading}
        open={!!openForm}
        onOpenChange={handleOpenChange}
        product={product}
        productMindboxId={productMindboxId}
        type={openForm === ReviewTab.Reviews ? 'review' : 'question'}
        skinTypes={skinTypes}
        skinProblems={skinProblems}
        ratingId={ratingId}
        ratings={ratings}
        userInfo={userInfo}
        isAuthorized={isAuthorized}
      />
    </>
  );
}
